import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import ErrorBoundary from './components/ErrorBoundary';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import { getHotDeals } from './api/listingClient';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';

// Route-based code splitting — lazy-load all pages except Login and Landing
const ProjectList = lazy(() => import('./pages/ProjectList'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const AnalysisList = lazy(() => import('./pages/AnalysisList'));
const AnalysisWizard = lazy(() => import('./pages/AnalysisWizard'));
const AnalysisDetail = lazy(() => import('./pages/AnalysisDetail'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const FlipAnalyzerPage = lazy(() => import('./pages/FlipAnalyzerPage'));
const ListingFeed = lazy(() => import('./pages/ListingFeed'));
const ListingDetail = lazy(() => import('./pages/ListingDetail'));
const PortfolioDashboard = lazy(() => import('./pages/PortfolioDashboard'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const AnalysisReport = lazy(() => import('./pages/AnalysisReport'));
const DistressedPipeline = lazy(() => import('./pages/DistressedPipeline'));
const DistressedLeadDetail = lazy(() => import('./pages/DistressedLeadDetail'));
const DistressedRoutes = lazy(() => import('./pages/DistressedRoutes'));
const MarketIntelligencePage = lazy(() => import('./pages/MarketIntelligencePage'));
const AgentConsole = lazy(() => import('./pages/AgentConsole'));
const ImprovementPlanner = lazy(() => import('./pages/ImprovementPlanner'));
const ReportingDashboard = lazy(() => import('./pages/ReportingDashboard'));
const CompAnalyzerPage = lazy(() => import('./pages/CompAnalyzerPage'));
const OffMarketResearchPage = lazy(() => import('./pages/OffMarketResearchPage'));
const DealDashboardPage = lazy(() => import('./pages/DealDashboardPage'));
const BuildManagementPage = lazy(() => import('./pages/BuildManagement'));
const MarketIntel = lazy(() => import('./pages/MarketIntel'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s before refetch
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ── Nav section label ──────────────────────────────────────────────────────

function NavSection({ label }: { label: string }) {
  return (
    <li
      style={{
        padding: '0.6rem 1.25rem 0.2rem',
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
        opacity: 0.7,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {label}
    </li>
  );
}

function AppContent() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hotDealsCount, setHotDealsCount] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef(0);

  useEffect(() => {
    if (!user) return;
    getHotDeals()
      .then((res) => setHotDealsCount(res.count))
      .catch(() => setHotDealsCount(0));
  }, [user]);

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: '100vh' }}>
        <div className="spinner" />
        Loading...
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="app-layout">
      <KeyboardShortcuts />
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Mobile Header */}
      <header className="mobile-header">
        <button
          className="hamburger-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle navigation menu"
          aria-expanded={sidebarOpen}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
        <span className="mobile-title">CCA</span>
      </header>

      {/* Sidebar Overlay (mobile) */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}

      {/* Sidebar */}
      <nav
        className={`sidebar ${sidebarOpen ? 'open' : ''}`}
        aria-label="Main navigation"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
          touchDeltaX.current = 0;
        }}
        onTouchMove={(e) => {
          if (touchStartX.current !== null) {
            touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
          }
        }}
        onTouchEnd={() => {
          if (touchDeltaX.current < -80 && sidebarOpen) closeSidebar();
          touchStartX.current = null;
          touchDeltaX.current = 0;
        }}
      >
        <div className="sidebar-brand">
          <h1>CCA — Deal Analyzer</h1>
          <p>Real estate investment analysis</p>
        </div>

        <ul className="sidebar-nav" role="list">
          {/* Home */}
          <li>
            <NavLink
              to="/"
              className={location.pathname === '/' ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#127968;</span> Home
            </NavLink>
          </li>

          {/* ── 1. Identify ──────────────────────────────────────────────── */}
          <NavSection label="1 · Identify" />
          <li>
            <NavLink
              to="/listings"
              className={location.pathname.startsWith('/listings') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#127969;</span> Listing Feed
              {hotDealsCount > 0 && <span className="sidebar-hot-badge">{hotDealsCount}</span>}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/distressed"
              className={location.pathname.startsWith('/distressed') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128270;</span> Distressed Pipeline
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/off-market"
              className={location.pathname.startsWith('/off-market') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128204;</span> Off-Market Research
            </NavLink>
          </li>

          {/* ── 2. Evaluate ──────────────────────────────────────────────── */}
          <NavSection label="2 · Evaluate" />
          <li>
            <NavLink
              to="/analyze"
              className={location.pathname.startsWith('/analyze') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128269;</span> Analysis Wizard
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/improvement-planner"
              className={location.pathname.startsWith('/improvement-planner') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128296;</span> Improvement Planner
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/comp-analyzer"
              className={location.pathname.startsWith('/comp-analyzer') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128202;</span> Comp Analyzer
            </NavLink>
          </li>

          {/* ── 3. Bid & Finance ─────────────────────────────────────────── */}
          <NavSection label="3 · Bid &amp; Finance" />
          <li>
            <NavLink
              to="/flip-calculator"
              className={location.pathname.startsWith('/flip-calculator') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128176;</span> Flip Analyzer
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/deal-dashboard"
              className={location.pathname.startsWith('/deal-dashboard') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128203;</span> Deal Dashboard
            </NavLink>
          </li>

          {/* ── 4. Build Management ──────────────────────────────────────── */}
          <NavSection label="4 · Build Management" />
          <li>
            <NavLink
              to="/build-management"
              className={location.pathname.startsWith('/build-management') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#127959;</span> Build Management
            </NavLink>
          </li>

          {/* ── 5. Reporting ─────────────────────────────────────────────── */}
          <NavSection label="5 · Reporting" />
          <li>
            <NavLink
              to="/reporting"
              className={location.pathname.startsWith('/reporting') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128196;</span> Pipeline Overview
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/market-intelligence"
              className={location.pathname.startsWith('/market-intelligence') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128201;</span> Market Intel
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/market-intel"
              className={location.pathname === '/market-intel' ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128225;</span> Parcl Intel
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/dashboard"
              className={location.pathname === '/dashboard' ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#128200;</span> Portfolio
            </NavLink>
          </li>

          {/* ── Settings + Agent ─────────────────────────────────────────── */}
          <li
            style={{
              marginTop: '0.5rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid var(--border)',
            }}
          >
            <NavLink
              to="/agents"
              className={location.pathname.startsWith('/agents') ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#129302;</span> Agent Console
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/settings"
              className={location.pathname === '/settings' ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span>&#9881;</span> Settings
            </NavLink>
          </li>
        </ul>

        {/* User info + sign out */}
        <div
          style={{
            marginTop: 'auto',
            padding: '1rem 1.25rem',
            borderTop: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
              marginBottom: '0.5rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user.email}
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={logout}
            style={{ width: '100%', justifyContent: 'center' }}
          >
            Sign out
          </button>
          <div
            style={{
              marginTop: '0.75rem',
              fontSize: '0.65rem',
              color: 'var(--text-muted)',
              textAlign: 'center',
              opacity: 0.6,
            }}
          >
            v{__APP_VERSION__}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main id="main-content" className="main-content">
        <Suspense
          fallback={
            <div className="loading-center">
              <div className="spinner" />
              Loading...
            </div>
          }
        >
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              {/* Projects / Import / Compare (legacy, kept intact) */}
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/import" element={<ImportPage />} />
              <Route path="/compare" element={<ComparePage />} />
              {/* 2 · Evaluate */}
              <Route path="/analyze" element={<AnalysisList />} />
              <Route path="/analyze/new" element={<AnalysisWizard />} />
              <Route path="/analyze/:id/inputs" element={<AnalysisWizard />} />
              <Route path="/analyze/:id/report" element={<AnalysisReport />} />
              <Route path="/analyze/:id" element={<AnalysisDetail />} />
              <Route
                path="/improvement-planner"
                element={
                  <div style={{ padding: 32, color: '#6b7280' }}>
                    Select an analysis from the{' '}
                    <a href="/analyze" style={{ color: '#1d4ed8' }}>
                      Analyze
                    </a>{' '}
                    page to open the Improvement Planner.
                  </div>
                }
              />
              <Route path="/improvement-planner/:analysisId" element={<ImprovementPlanner />} />
              <Route path="/comp-analyzer" element={<CompAnalyzerPage />} />
              {/* 1 · Identify */}
              <Route path="/listings" element={<ListingFeed />} />
              <Route path="/listings/:id" element={<ListingDetail />} />
              <Route path="/distressed" element={<DistressedPipeline />} />
              <Route path="/distressed/routes" element={<DistressedRoutes />} />
              <Route path="/distressed/:id" element={<DistressedLeadDetail />} />
              <Route path="/off-market" element={<OffMarketResearchPage />} />
              {/* 3 · Bid & Finance */}
              <Route path="/flip-calculator" element={<FlipAnalyzerPage />} />
              <Route path="/deal-dashboard" element={<DealDashboardPage />} />
              {/* 4 · Build Management */}
              <Route path="/build-management" element={<BuildManagementPage />} />
              {/* 5 · Reporting */}
              <Route path="/reporting" element={<ReportingDashboard />} />
              <Route path="/market-intelligence" element={<MarketIntelligencePage />} />
              <Route path="/market-intel" element={<MarketIntel />} />
              <Route path="/dashboard" element={<PortfolioDashboard />} />
              {/* Settings + Agent */}
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/agents" element={<AgentConsole />} />
            </Routes>
          </ErrorBoundary>
        </Suspense>
      </main>

      {/* Bottom Navigation (mobile only, hidden on desktop via CSS) */}
      <nav className="bottom-nav" aria-label="Quick navigation">
        <NavLink
          to="/"
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
          end
        >
          <span className="bottom-nav-icon">&#127968;</span>
          <span className="bottom-nav-label">Home</span>
        </NavLink>
        <NavLink
          to="/analyze"
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
        >
          <span className="bottom-nav-icon">&#128269;</span>
          <span className="bottom-nav-label">Analyze</span>
        </NavLink>
        <NavLink
          to="/listings"
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
        >
          <span className="bottom-nav-icon">&#127969;</span>
          <span className="bottom-nav-label">Listings</span>
        </NavLink>
        <NavLink
          to="/market-intelligence"
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
        >
          <span className="bottom-nav-icon">&#128201;</span>
          <span className="bottom-nav-label">Market</span>
        </NavLink>
        <NavLink
          to="/settings"
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
        >
          <span className="bottom-nav-icon">&#9881;</span>
          <span className="bottom-nav-label">Settings</span>
        </NavLink>
      </nav>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PreferencesProvider>
          <AppContent />
        </PreferencesProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
